import type { Subject, Observable } from 'rxjs';
import { ReplaySubject, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import type { Logger } from '@bayer-int/portletce-shared';
import { isString, Signal } from '@bayer-int/portletce-shared';
import { isLeft } from 'fp-ts/Either';
import * as t from 'io-ts';
import { fromActions } from '@rxsv/core';

import { FilterValue, FilterDataArray, Settings } from 'portlet/models';

import type { ExternalAppAction, PublicAction, AppState } from './store';
import { AppAction, Selectors } from './store';
import { createValidationError } from './common/errors';

export function createInstanceApi(
    getState: () => AppState,
    logger: Logger,
    uniqueid?: string | undefined,
) {
    const events$ = new ReplaySubject<AppAction>();

    const dispatchFilter = createDispatchFilterWithParser(events$, FilterValue.tryParse, uniqueid);

    const dispatchFilterOfType = {
        dimension: createDispatchFilterWithParser(events$, FilterValue.tryParseDimension, uniqueid),
        date: createDispatchFilterWithParser(events$, FilterValue.tryParseDate, uniqueid),
        dateRange: createDispatchFilterWithParser(events$, FilterValue.tryParseDateRange, uniqueid),
        hierarchy: createDispatchFilterWithParser(events$, FilterValue.tryParseHierarchy, uniqueid),
        scale: createDispatchFilterWithParser(events$, FilterValue.tryParseScale, uniqueid),
        smartViewTag: createDispatchFilterWithParser(
            events$,
            FilterValue.tryParseSmartViewTag,
            uniqueid,
        ),
        role: createDispatchFilterWithParser(events$, FilterValue.tryParseRole, uniqueid),
    };

    function setSignal(name: unknown, value: unknown) {
        return setSignals([{ name, value }]);
    }

    function setSetting(key: unknown, value: unknown) {
        return setSettings([{ key, value }]);
    }

    function setSettings(settings: unknown) {
        const validatedSettings = t.array(Settings.keyValueCodec).decode(settings);
        if (isLeft(validatedSettings))
            return logger.warn(createValidationError(validatedSettings.left));
        events$.next(AppAction.filters.CHANGE_SETTING_VALUES(validatedSettings.right));
    }

    function setSignals(signals: unknown) {
        const validatedSignals = t.array(Signal.codec).decode(signals);

        if (isLeft(validatedSignals))
            return logger.warn(createValidationError(validatedSignals.left));

        events$.next(AppAction.filters.SET_SIGNALS(validatedSignals.right));
    }

    function setFilters(filters: unknown) {
        const validatedFilters = FilterDataArray.codec.decode(filters);

        if (isLeft(validatedFilters))
            return logger.warn(createValidationError(validatedFilters.left));

        events$.next(AppAction.filters.SET_ALL_FILTER_VALUES(validatedFilters.right));
    }

    function reloadData() {
        events$.next(AppAction.view.RELOAD_DATA(new Date().toISOString()));
    }

    function downloadData() {
        events$.next(AppAction.view.DOWNLOAD_DATA());
    }

    function createDispatchFilterWithParser(
        subject: Subject<AppAction>,
        parser: (value: unknown) => FilterValue | null,
        sender = '',
    ) {
        return (item: unknown, rawValue: unknown) => {
            const value = parser(rawValue);

            if (!isString(item)) {
                logger.error(`'filterItem' must be a string`);
                return null;
            }
            if (!FilterValue.codec.is(value)) {
                logger.error(`Failed to parse filter value:`, rawValue);
                return null;
            }

            const action: ExternalAppAction = {
                ...AppAction.filters.SET_FILTER_VALUE({ item, value }),
                sender,
            };

            subject.next(action);
        };
    }

    return {
        events$: events$.asObservable(),
        uniqueId: () => uniqueid,
        dispatchFilter,
        dispatchFilterOfType,
        setSignal,
        setSignals,
        setSetting,
        setSettings,
        setFilters,
        reloadData,
        downloadData,

        getters: {
            get filters() {
                return Selectors.filterSelector(getState());
            },
            get settings() {
                return Selectors.settingValuesSelector(getState());
            },
            get config() {
                return Selectors.configSelector(getState());
            },
            get debug() {
                return Selectors.portletDebugInfoSelector(getState());
            },
            get isDataToDownload() {
                return !!Selectors.dataToDownloadSelector(getState());
            },
        },
    };
}

export function forwardFilter(action$: Observable<ExternalAppAction>): Observable<PublicAction> {
    const setFilter$ = action$.pipe(
        fromActions(AppAction.filters.CHANGE_FILTER_VALUE),
        map(({ payload }) => AppAction.filters.SET_FILTER_VALUE(payload)),
    );

    const setFilters$ = action$.pipe(
        fromActions(AppAction.filters.CHANGE_FILTER_VALUES),
        map(({ payload }) => AppAction.filters.SET_FILTER_VALUES(payload)),
    );

    const clearFilter$ = action$.pipe(
        fromActions(AppAction.filters.CLEAR_FILTER_REQUEST),
        map(({ payload }) => AppAction.filters.CLEAR_FILTER(payload)),
    );

    const clearFilters$ = action$.pipe(
        fromActions(AppAction.filters.CLEAR_FILTERS_REQUEST),
        map(({ payload }) => AppAction.filters.CLEAR_FILTERS(payload)),
    );

    return merge(setFilter$, setFilters$, clearFilter$, clearFilters$);
}

export function applyFilterMapping(mapping: Record<string, string> = {}) {
    return <A extends PublicAction>(action$: Observable<A>): Observable<A> =>
        action$.pipe(
            map(action => {
                switch (action.type) {
                    case 'CHANGE_FILTER_VALUE':
                    case 'SET_FILTER_VALUE':
                    case 'CLEAR_FILTER':
                    case 'CLEAR_FILTER_REQUEST':
                        return {
                            ...action,
                            payload: {
                                ...action.payload,
                                item: mapping[action.payload.item] || action.payload.item,
                            },
                        };
                    case 'CHANGE_FILTER_VALUES':
                    case 'SET_FILTER_VALUES':
                    case 'CLEAR_FILTERS':
                    case 'CLEAR_FILTERS_REQUEST':
                        return {
                            ...action,
                            payload: action.payload.map(filter => ({
                                ...filter,
                                item: mapping[filter.item] || filter.item,
                            })),
                        };

                    default:
                        return action;
                }
            }),
        );
}
